import { ftz } from 'utils/userTimezone';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Conversation, Message, ChatUser } from 'utils/chatTypes';
import { composeDisplayName } from 'utils/displayName';
import { useChatStore } from 'utils/chatStore';
import { useBusinessStore } from 'utils/useBusinessStore';
import { subscribeToMessages, sendMessage, sendImageMessage, sendFileMessage, editMessage, deleteMessage, toggleReaction } from 'utils/chatService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Send, Users, User, Building, Paperclip, File, Download, X, ArrowLeft, MessageSquare, MessageCircle, Loader2, Phone, PhoneOff, PhoneMissed, Smile, Edit2, Trash2, Check, Heart, Bookmark, Copy, CheckSquare, Square, Reply, MapPin, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { getFirestore, getDocs, collection, query, where } from 'firebase/firestore';
import { firebaseApp, firestore } from 'app';
import brain from 'brain';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';

interface Props {
  conversation: Conversation;
  currentUser: ChatUser;
  hideMobileHeader?: boolean;
  showBackButton?: boolean;
  onBackClick?: () => void;
  disableInput?: boolean;
  disableReason?: string;
}

// Helper function to format user name as "FirstName LastInitial"
const formatUserName = (fullName: string): string => {
  const nameParts = fullName.trim().split(' ');
  if (nameParts.length === 1) {
    return nameParts[0]; // Just first name if no last name
  }
  const firstName = nameParts[0];
  const lastName = nameParts[nameParts.length - 1];
  const lastInitial = lastName.charAt(0).toUpperCase();
  return `${firstName} ${lastInitial}`;
};

export const MessageThread: React.FC<Props> = ({ conversation, currentUser, hideMobileHeader, showBackButton, onBackClick, disableInput, disableReason }) => {
  const { messages, setMessages, setError } = useChatStore();
  const { businessDetails, loadBusinessDetails } = useBusinessStore();
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ url: string; name: string } | null>(null);
  // Image-viewer modal — opened when a chat image thumbnail is clicked.
  // Stores the full-size data URL + filename for the lightbox.
  const [viewerImage, setViewerImage] = useState<{ url: string; name: string } | null>(null);
  const [savingNoteFor, setSavingNoteFor] = useState<string | null>(null);

  // ── Save-to-Notes (chat → client profile) ───────────────────────────
  // For client_discussion chats the note attaches to the conversation's
  // subject client (conversation.clientId). For all other chat types the
  // note attaches to the message sender (when the sender is a client).
  // Admins can save any message; staff can save in conversations they
  // are part of.
  const handleSaveToNotes = async (message: any) => {
    if (!currentUser?.id) return;
    try {
      setSavingNoteFor(message.id);
      const conv: any = conversation as any;
      let targetClientId: string | null = null;
      if (conv?.type === 'client_discussion' && conv?.clientId) {
        targetClientId = conv.clientId;
      } else if (message.senderId) {
        // For non-discussion chats, save against the sender. Only meaningful
        // if the sender is a client — admins can also use this to capture
        // their own statements in client-context chats.
        targetClientId = message.senderId;
      }
      if (!targetClientId) {
        toast.error('Could not determine which client to save this note to');
        return;
      }
      const senderName = message.senderName || conv?.participantDetails?.[message.senderId]?.name || 'Unknown';
      const sentAt = message.createdAt
        ? (message.createdAt instanceof Date
            ? message.createdAt.toISOString()
            : new Date(message.createdAt as any).toISOString())
        : null;
      // For image messages, the text body would otherwise just be the
      // placeholder "[image]"; we still want a readable caption, but the
      // actual picture is preserved on the note via attachmentUrl so the
      // Client Notes modal can render it inline.
      const isImage = message.type === 'image';
      const attachmentUrl = isImage
        ? (message.attachmentUrl || message.content || null)
        : null;
      const bodyText = message.content
        || (isImage ? '[image]' : '');
      const content = `${senderName}: ${bodyText}`.trim();

      const apiUrl = (import.meta as any).env?.VITE_API_URL
        || (import.meta as any).env?.REACT_APP_BACKEND_URL
        || '';
      const token = await (window as any).firebase?.auth?.currentUser?.getIdToken?.();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const resp = await brain.create_client_note({
        clientId: targetClientId,
        content,
        source: 'chat',
        chatContext: {
          conversationId: message.conversationId,
          messageId: message.id,
          senderId: message.senderId,
          senderName,
          sentAt,
        },
        attachmentUrl,
      } as any);
      if (!(resp as any).ok) {
        throw new Error(`Status ${(resp as any).status}`);
      }
      toast.success('Saved to client notes');
    } catch (err) {
      console.error('Failed to save to notes', err);
      toast.error('Failed to save to notes');
    } finally {
      setSavingNoteFor(null);
    }
  };

  // ── Copy-to-Clipboard ───────────────────────────────────────────────
  // Lets the user grab a message's content along with its sender + send
  // timestamp so they can paste it into an appointment note (existing
  // or new). Format mirrors what the audit log expects:
  //   [Mon 24 Feb 2026, 3:45 PM] Jane D: hello world
  const handleCopyMessage = async (message: any) => {
    try {
      const conv: any = conversation as any;
      const senderName = message.senderName
        || conv?.participantDetails?.[message.senderId]?.name
        || 'Unknown';
      const sentDate: Date | null = message.createdAt
        ? (message.createdAt instanceof Date
            ? message.createdAt
            : new Date(message.createdAt as any))
        : null;
      const stamp = sentDate
        ? ftz(sentDate, 'EEE, dd MMM yyyy, h:mm a')
        : 'Unknown time';
      const body = message.content || (message.type === 'image' ? '[image]' : '');
      const formatted = `[${stamp}] ${senderName}: ${body}`;
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(formatted);
      } else {
        // Fallback for older browsers / insecure contexts
        const ta = document.createElement('textarea');
        ta.value = formatted;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success('Copied — paste into an appointment');
    } catch (err) {
      console.error('Failed to copy message', err);
      toast.error('Failed to copy');
    }
  };

  // ── Multi-select copy ──────────────────────────────────────────────
  // Lets the user pick several messages and copy them as a bundled
  // chronological timeline — handy for stitching an incident report or
  // a quick handover note into an appointment.
  //
  // Selection rules:
  //   • Click the select-toggle to add/remove a single message.
  //   • Shift-click to select every message between the last toggle and
  //     the clicked one (inclusive).
  const [selectedMessageIds, setSelectedMessageIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);

  const formatStampForBundle = (date: Date | null): string => {
    if (!date) return 'Unknown time';
    return ftz(date, 'EEE, dd MMM yyyy, h:mm a');
  };

  const toggleMessageSelection = (messageId: string, withShift: boolean) => {
    setSelectedMessageIds((prev) => {
      const next = new Set(prev);
      // Shift-click range extends from lastSelectedId to messageId.
      if (withShift && lastSelectedId && lastSelectedId !== messageId) {
        const ordered = conversationMessages.map((m) => m.id);
        const a = ordered.indexOf(lastSelectedId);
        const b = ordered.indexOf(messageId);
        if (a >= 0 && b >= 0) {
          const [lo, hi] = a < b ? [a, b] : [b, a];
          for (let i = lo; i <= hi; i++) next.add(ordered[i]);
          return next;
        }
      }
      // Simple toggle.
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
    setLastSelectedId(messageId);
  };

  const clearSelection = () => {
    setSelectedMessageIds(new Set());
    setLastSelectedId(null);
  };

  const copySelectedMessages = async () => {
    if (selectedMessageIds.size === 0) return;
    try {
      const conv: any = conversation as any;
      // Preserve chronological order — conversationMessages is already
      // newest-or-oldest depending on the subscriber; iterate it directly.
      const lines = conversationMessages
        .filter((m) => selectedMessageIds.has(m.id))
        .map((m) => {
          const senderName = m.senderName
            || conv?.participantDetails?.[m.senderId]?.name
            || 'Unknown';
          const d = m.createdAt
            ? (m.createdAt instanceof Date ? m.createdAt : new Date(m.createdAt as any))
            : null;
          const body = m.content || (m.type === 'image' ? '[image]' : '');
          return `[${formatStampForBundle(d)}] ${senderName}: ${body}`;
        });
      const bundle = lines.join('\n');
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(bundle);
      } else {
        const ta = document.createElement('textarea');
        ta.value = bundle;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(`Copied ${lines.length} message${lines.length === 1 ? '' : 's'} — paste into an appointment`);
      clearSelection();
    } catch (err) {
      console.error('Failed to copy selected messages', err);
      toast.error('Failed to copy selection');
    }
  };

  // ── Save bundle to Client Notes ────────────────────────────────────
  // Bundles every selected message into ONE note (same formatting as
  // the clipboard copy). If any image messages are part of the bundle,
  // the FIRST image's URL is attached so the picture also renders on
  // the client profile. Same target-client rule as single-save.
  const [savingBundleNote, setSavingBundleNote] = useState(false);
  const saveSelectedAsNote = async () => {
    if (selectedMessageIds.size === 0) return;
    const conv: any = conversation as any;
    const selected = conversationMessages.filter((m) => selectedMessageIds.has(m.id));
    if (selected.length === 0) return;

    // Target client mirrors single-save logic.
    let targetClientId: string | null = null;
    if (conv?.type === 'client_discussion' && conv?.clientId) {
      targetClientId = conv.clientId;
    } else if (selected[0].senderId) {
      targetClientId = selected[0].senderId;
    }
    if (!targetClientId) {
      toast.error('Could not determine which client to save this bundle to');
      return;
    }

    try {
      setSavingBundleNote(true);
      const lines = selected.map((m) => {
        const senderName = m.senderName
          || conv?.participantDetails?.[m.senderId]?.name
          || 'Unknown';
        const d = m.createdAt
          ? (m.createdAt instanceof Date ? m.createdAt : new Date(m.createdAt as any))
          : null;
        const body = m.content || (m.type === 'image' ? '[image]' : '');
        return `[${formatStampForBundle(d)}] ${senderName}: ${body}`;
      });
      const content = lines.join('\n');

      // First image attachment in the bundle gets attached on the note.
      const firstImage = selected.find((m) => m.type === 'image');
      const attachmentUrl = firstImage
        ? (firstImage.attachmentUrl || firstImage.content || null)
        : null;

      const earliest = selected[0]?.createdAt;
      const sentAt = earliest
        ? (earliest instanceof Date ? earliest.toISOString() : new Date(earliest as any).toISOString())
        : null;

      const resp = await brain.create_client_note({
        clientId: targetClientId,
        content,
        source: 'chat',
        chatContext: {
          conversationId: selected[0].conversationId,
          messageId: selected[0].id,
          senderId: selected[0].senderId,
          senderName: selected[0].senderName,
          sentAt,
        },
        attachmentUrl,
      } as any);
      if (!(resp as any).ok) {
        throw new Error(`Status ${(resp as any).status}`);
      }
      toast.success(`Saved ${selected.length} message${selected.length === 1 ? '' : 's'} to client notes`);
      clearSelection();
    } catch (err) {
      console.error('Failed to save bundle to notes', err);
      toast.error('Failed to save bundle to notes');
    } finally {
      setSavingBundleNote(false);
    }
  };
  const [participantDetails, setParticipantDetails] = useState<{[userId: string]: {name: string, photoURL?: string, role?: string}}>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [reactionPickerMessageId, setReactionPickerMessageId] = useState<string | null>(null);
  const [replyTarget, setReplyTarget] = useState<Message | null>(null);
  const reactionPickerRef = useRef<HTMLDivElement>(null);
  
  const conversationMessages = messages[conversation.id] || [];

  // Mobile keyboard detection with improved iOS handling
  useEffect(() => {
    let initialViewportHeight = window.visualViewport?.height || window.innerHeight;
    
    const handleViewportChange = () => {
      const currentHeight = window.visualViewport?.height || window.innerHeight;
      const heightDifference = initialViewportHeight - currentHeight;
      
      // Consider keyboard visible if viewport height decreased by more than 150px
      setIsKeyboardVisible(heightDifference > 150);
      
      // On iOS, when keyboard opens, ensure input stays visible
      if (heightDifference > 150 && inputRef.current) {
        setTimeout(() => {
          // Use more aggressive scrolling for iOS
          inputRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest',
            inline: 'nearest'
          });
          
          // Additional fallback: scroll window to bottom
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth'
          });
        }, 100);
      }
    };

    const handleFocusIn = (e: FocusEvent) => {
      if (inputRef.current && e.target === inputRef.current) {
        // Mark keyboard as potentially visible immediately
        setIsKeyboardVisible(true);
        
        // Multiple attempts to ensure input visibility (iOS can be stubborn)
        const scrollAttempts = [100, 300, 600];
        scrollAttempts.forEach(delay => {
          setTimeout(() => {
            // Try scrolling the input into view
            inputRef.current?.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'end',
              inline: 'nearest'
            });
            
            // Also try scrolling the container
            if (inputContainerRef.current) {
              const container = inputContainerRef.current;
              const rect = container.getBoundingClientRect();
              
              // If input is not fully visible, scroll window
              if (rect.bottom > window.innerHeight) {
                window.scrollBy({
                  top: rect.bottom - window.innerHeight + 20,
                  behavior: 'smooth'
                });
              }
            }
          }, delay);
        });
      }
    };

    const handleFocusOut = () => {
      // Reset when input loses focus
      setTimeout(() => {
        setIsKeyboardVisible(false);
      }, 100);
    };

    // Listen for visual viewport changes (more reliable for mobile)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);
    } else {
      // Fallback for older browsers
      window.addEventListener('resize', handleViewportChange);
    }

    // Listen for input focus events
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      } else {
        window.removeEventListener('resize', handleViewportChange);
      }
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // Load business details when component mounts
  useEffect(() => {
    loadBusinessDetails();
  }, [loadBusinessDetails]);

  // Subscribe to messages for this conversation
  useEffect(() => {
    const unsubscribe = subscribeToMessages(conversation.id, (newMessages) => {
      setMessages(conversation.id, newMessages);
    });

    return () => {
      unsubscribe();
    };
  }, [conversation.id, setMessages]);

  // Track if we're currently marking messages to prevent duplicate calls
  const markingAsReadRef = useRef(false);
  const lastMarkedMessageIdRef = useRef<string | null>(null);

  // Mark messages as read when conversation is opened or messages change
  useEffect(() => {
    if (conversationMessages.length > 0) {
      // Get the last message ID (most recent)
      const lastMessage = conversationMessages[conversationMessages.length - 1];
      
      // Skip if already marking or if we just marked this exact message
      if (markingAsReadRef.current || lastMarkedMessageIdRef.current === lastMessage.id) {
        return;
      }
      
      markingAsReadRef.current = true;
      lastMarkedMessageIdRef.current = lastMessage.id;
      
      import('utils/chatService').then(({ markMessagesAsRead }) => {
        // Pass the last visible message ID
        markMessagesAsRead(conversation.id, currentUser.id, lastMessage.id)
          .catch(err => {
            console.error('Error marking messages as read:', err);
          })
          .finally(() => {
            markingAsReadRef.current = false;
          });
      });
    }
  }, [conversation.id, currentUser.id, conversationMessages.length]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationMessages]);

  // Load participant details when conversation changes
  useEffect(() => {
    const loadParticipantDetails = async () => {
      const otherUserIds = new Set<string>();
      
      // Collect user IDs that are not the current user
      conversation.participants.forEach(userId => {
        if (userId !== currentUser.id) {
          otherUserIds.add(userId);
        }
      });
      
      if (otherUserIds.size === 0) {
        // If only current user, set empty details
        setParticipantDetails({
          [currentUser.id]: {
            name: currentUser.name,
            photoURL: currentUser.photoURL,
            role: currentUser.role
          }
        });
        return;
      }
      
      const details: {[userId: string]: {name: string, photoURL?: string, role?: string}} = {};
      
      // Always include current user details
      details[currentUser.id] = {
        name: currentUser.name,
        photoURL: currentUser.photoURL,
        role: currentUser.role
      };
      
      try {
        const db = firestore;
        
        // Fetch details for other users only
        const userPromises = Array.from(otherUserIds).map(async (userId) => {
          try {
            const userDoc = await getDocs(query(collection(db, 'users'), where('__name__', '==', userId)));
            if (!userDoc.empty) {
              const userData = userDoc.docs[0].data();
              details[userId] = {
                name: composeDisplayName(userData),
                photoURL: userData.photoURL,
                role: userData.role
              };
            } else {
              details[userId] = { 
                name: 'Unknown User',
                photoURL: undefined,
                role: undefined
              };
            }
          } catch (error) {
            console.error(`Error loading details for user ${userId}:`, error);
            details[userId] = { 
              name: 'Unknown User',
              photoURL: undefined,
              role: undefined
            };
          }
        });
        
        await Promise.all(userPromises);
        setParticipantDetails(details);
      } catch (error) {
        console.error('Error loading participant details:', error);
      }
    };
    
    loadParticipantDetails();
  }, [conversation.id, conversation.participants, currentUser.id, currentUser.name, currentUser.photoURL, currentUser.role]);

  // Reply preview text, built per the native app's rules: original text
  // (trimmed, 200 chars) → else "Photo" for images → else file name.
  const messageSnippet = (m: Message): string => {
    const anyM: any = m;
    const t = (anyM.content || anyM.text || '').trim();
    if (t && t !== '📷 Image' && t !== '📹 Video') return t.slice(0, 200);
    if (m.type === 'image' || anyM.fileType === 'image') return 'Photo';
    return anyM.attachmentName || anyM.fileName || 'Attachment';
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedImage) return;

    setIsSending(true);
    try {
      if (selectedImage) {
        // Send image first
        const file = new (window as any).File([await (await fetch(selectedImage.url)).blob()], selectedImage.name);
        await sendImageMessage(conversation.id, file, currentUser);
        toast.success('Image sent successfully');
        setSelectedImage(null);
      }
      
      if (newMessage.trim()) {
        const replyTo = replyTarget
          ? {
              id: replyTarget.id,
              text: messageSnippet(replyTarget),
              senderName: replyTarget.senderName,
              type: replyTarget.type || 'text',
            }
          : undefined;
        await sendMessage(conversation.id, newMessage.trim(), currentUser, replyTo);
        setNewMessage('');
        setReplyTarget(null);
      }
      
      setError(null);
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error instanceof Error ? error.message : 'Failed to send message');
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setNewMessage((prev) => prev + emojiData.emoji);
    // Keep emoji picker open for multiple selections
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);


  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(event.target as Node)) {
        setReactionPickerMessageId(null);
      }
    };

    if (reactionPickerMessageId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [reactionPickerMessageId]);

  const handleEditMessage = async (messageId: string) => {
    if (!editedContent.trim()) {
      toast.error('Message cannot be empty');
      return;
    }

    try {
      console.log('Attempting to edit message:', {
        messageId,
        conversationId: conversation.id,
        currentUserId: currentUser.id,
        newContent: editedContent
      });
      await editMessage(conversation.id, messageId, editedContent);
      setEditingMessageId(null);
      setEditedContent('');
      toast.success('Message updated');
    } catch (error) {
      console.error('Error editing message:', error);
      console.error('Full error details:', JSON.stringify(error, null, 2));
      toast.error(error instanceof Error ? error.message : 'Failed to edit message');
    }
  };

  const handleDeleteMessage = async (messageId: string) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    setIsDeleting(messageId);
    try {
      await deleteMessage(conversation.id, messageId);
      toast.success('Message deleted');
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Failed to delete message');
    } finally {
      setIsDeleting(null);
    }
  };

  const startEdit = (message: Message) => {
    setEditingMessageId(message.id);
    setEditedContent(message.content);
  };

  const cancelEdit = () => {
    setEditingMessageId(null);
    setEditedContent('');
  };


  const handleToggleReaction = async (messageId: string, emoji: string) => {
    try {
      await toggleReaction(conversation.id, messageId, currentUser.id, emoji);
    } catch (error) {
      console.error('Error toggling reaction:', error);
      toast.error('Failed to react to message');
    }
  };


  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file size (max 10MB)
    const masmize = 10 * 1024 * 1024; // 10MB
    if (file.size > masmize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setIsUploading(true);
    try {
      if (file.type.startsWith('image/')) {
        await sendImageMessage(conversation.id, file, currentUser);
        toast.success('Image sent successfully');
      } else {
        await sendFileMessage(conversation.id, file, currentUser);
        toast.success('File sent successfully');
      }
      setError(null);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
      setError('Failed to upload file');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const getConversationHeader = () => {
    let icon = MessageSquare;
    let name = conversation.name || 'Unnamed Conversation';
    let avatarSrc: string | undefined;
    let participantList = '';

    if (conversation.type === 'one_on_one') {
      icon = User;
      name = conversation.otherUserName || 'Unknown User';
      avatarSrc = conversation.otherUserAvatar;
      participantList = conversation.otherUserName || 'Unknown User';
    } else if (conversation.type === 'admin_client') {
      icon = MessageCircle;
      
      // Find the client in the participants
      const clientParticipant = conversation.participants.find(userId => {
        const participant = participantDetails[userId];
        return participant && (participant.role === 'client' || participant.role === 'limited_access');
      });
      
      if (clientParticipant && participantDetails[clientParticipant]) {
        const clientDetails = participantDetails[clientParticipant];
        name = clientDetails.name;
        avatarSrc = clientDetails.photoURL;
        participantList = clientDetails.name;
      } else {
        // If we don't have role info yet, try to find client by excluding current user and known staff
        const potentialClient = conversation.participants.find(userId => {
          if (userId === currentUser.id) return false; // Not the current user
          const participant = participantDetails[userId];
          // If we have role info and it's staff/admin, exclude it
          if (participant && participant.role && (participant.role === 'admin' || participant.role === 'staff')) {
            return false;
          }
          return true; // Likely a client
        });
        
        if (potentialClient && participantDetails[potentialClient]) {
          const clientDetails = participantDetails[potentialClient];
          name = clientDetails.name;
          avatarSrc = clientDetails.photoURL;
          participantList = clientDetails.name;
        } else {
          name = conversation.name || 'Client Chat';
          participantList = 'Loading client info...';
        }
      }
    } else {
      // Group chat or other types
      const participantNames = conversation.participants
        .map(userId => {
          if (userId === currentUser.id) {
            return formatUserName(currentUser.name);
          }
          return participantDetails[userId] ? formatUserName(participantDetails[userId].name) : 'Loading...';
        })
        .filter(name => name !== 'Loading...'); // Don't show loading names
      
      participantList = participantNames.length > 0 ? participantNames.join(', ') : 'Loading participants...';
    }

    return { icon, name, avatarSrc, participantList };
  };

  const { icon, name, avatarSrc, participantList } = getConversationHeader();

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Sticky Header - Always visible at top */}
      {!hideMobileHeader && (
        <div className="sticky top-0 z-20 p-4 border-b border-gray-200 bg-white shadow-sm flex-shrink-0">
          <div className="flex items-center space-x-3">
            {showBackButton && (
              <Button
                onClick={onBackClick}
                variant="ghost"
                size="sm"
                className="p-2"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            )}
            <Avatar>
              <AvatarImage src={avatarSrc} alt={name} />
              <AvatarFallback className="bg-teal-100 text-teal-800">
                {React.createElement(icon, { className: "h-5 w-5" })}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-medium text-gray-900">{name}</h3>
              <p className="text-sm text-gray-500">
                {participantList}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable Messages Area */}
      <div className={`flex-1 overflow-y-auto min-h-0 relative ${
        isKeyboardVisible ? 'pb-32' : 'pb-4'
      }`}>
        {/* Multi-select toolbar — sticky at the top of the messages
            scroll container. Shows count + Copy + Clear when ≥1
            messages are selected. */}
        {selectedMessageIds.size > 0 && (
          <div
            className="sticky top-0 z-20 mx-4 mt-2 mb-1 flex items-center justify-between gap-3 px-3 py-2 bg-teal-50 border border-teal-200 rounded-md shadow-sm"
            data-testid="multi-select-toolbar"
          >
            <div className="text-sm font-medium text-teal-800">
              {selectedMessageIds.size} message{selectedMessageIds.size === 1 ? '' : 's'} selected
              <span className="ml-2 text-xs text-teal-600 font-normal hidden sm:inline">
                Shift-click for range
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={saveSelectedAsNote}
                disabled={savingBundleNote}
                className="bg-amber-500 hover:bg-amber-600 text-white h-8"
                data-testid="multi-select-save-notes-btn"
              >
                {savingBundleNote ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Bookmark className="h-4 w-4 mr-1.5" />
                )}
                Save bundle to notes
              </Button>
              <Button
                size="sm"
                onClick={copySelectedMessages}
                className="bg-teal-600 hover:bg-teal-700 text-white h-8"
                data-testid="multi-select-copy-btn"
              >
                <Copy className="h-4 w-4 mr-1.5" />
                Copy bundle
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={clearSelection}
                className="h-8 text-teal-700 hover:bg-teal-100"
                data-testid="multi-select-clear-btn"
              >
                Clear
              </Button>
            </div>
          </div>
        )}
        <div className="p-4 space-y-4">
          {conversationMessages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            (() => {
              // Calculate which message is the last read by each participant
              // We need to show read avatars only on the last message they've read
              const lastReadByUser: Map<string, string> = new Map(); // userId -> lastReadMessageId
              
              // Go through messages in order and track the last read message for each user
              conversationMessages.forEach((message) => {
                const readByArray = message.readBy || [];
                if (Array.isArray(readByArray)) {
                  readByArray.forEach((r: any) => {
                    if (r.userId && r.userId !== currentUser.id) {
                      // Update to this message as the last read for this user
                      lastReadByUser.set(r.userId, message.id);
                    }
                  });
                }
              });

              // Both the native app and the web maintain read state on the
              // CONVERSATION doc (`lastReadBy.{uid}` timestamps) — per-message
              // `readBy` arrays are never written by the mobile build. Derive
              // receipts from the conversation-level state so messages read on
              // the app show as read here too. Anchor each reader on the last
              // message they've read that they didn't send themselves, so the
              // avatar lands under the other party's bubble.
              const convLastRead = conversation.lastReadBy || {};
              Object.entries(convLastRead).forEach(([usrId, readAtRaw]) => {
                if (!usrId || usrId === currentUser.id) return;
                const readAt = readAtRaw instanceof Date ? readAtRaw : new Date(readAtRaw as any);
                if (isNaN(readAt.getTime())) return;
                for (let i = conversationMessages.length - 1; i >= 0; i--) {
                  const m = conversationMessages[i];
                  if (m.senderId === usrId) continue;
                  const c = m.createdAt instanceof Date ? m.createdAt : new Date(m.createdAt as any);
                  if (!isNaN(c.getTime()) && c.getTime() <= readAt.getTime()) {
                    lastReadByUser.set(usrId, m.id);
                    break;
                  }
                }
              });
              
              return conversationMessages.map((message, _mIdx) => {
              // Day divider: render a small pill above the first message
              // of every new calendar day so long backscrolls have clear
              // calendar boundaries.
              const _prev = _mIdx > 0 ? conversationMessages[_mIdx - 1] : null;
              const _dividerLabel = (() => {
                const d = message.createdAt as Date;
                if (!(d instanceof Date) || isNaN(d.getTime())) return null;
                const dayKey = (x: Date) => ftz(x, 'yyyy-MM-dd');
                if (_prev) {
                  const p = _prev.createdAt as Date;
                  if (
                    p instanceof Date &&
                    !isNaN(p.getTime()) &&
                    dayKey(p) === dayKey(d)
                  ) {
                    return null; // same day as previous message
                  }
                }
                const now = new Date();
                const yesterday = new Date(now.getTime() - 86400000);
                if (dayKey(d) === dayKey(now)) return 'Today';
                if (dayKey(d) === dayKey(yesterday)) return 'Yesterday';
                const inSameYear = ftz(d, 'yyyy') === ftz(now, 'yyyy');
                return ftz(d, inSameYear ? 'EEE d MMMM' : 'EEE d MMMM yyyy');
              })();
              const isOwnMessage = message.senderId === currentUser.id;
              const senderDetails = participantDetails[message.senderId] || {
                name: 'Unknown User',
                photoURL: undefined,
                role: undefined
              };
              const sender = {
                id: message.senderId,
                name: senderDetails.name,
                photoURL: senderDetails.photoURL,
                role: senderDetails.role
              };

              // Handle message display based on conversation type and current user role
              let displayName = sender.name;
              let treatAsOwnMessage = isOwnMessage; // Default behavior
              
              if (conversation.type === 'admin_client') {
                if (currentUser.role === 'client') {
                  // Clients see "TAP Staff" for all non-client messages
                  displayName = isOwnMessage ? 'You' : 'TAP Staff';
                  treatAsOwnMessage = isOwnMessage;
                } else {
                  // Staff/Admin view: ALL staff messages on right side, client messages on left
                  const isClientMessage = senderDetails.role === 'client' || senderDetails.role === 'limited_access';
                  
                  if (isClientMessage) {
                    displayName = sender.name; // Show client name
                    treatAsOwnMessage = false; // Left side (blue)
                  } else {
                    displayName = isOwnMessage ? 'You' : formatUserName(sender.name); // Show staff names
                    treatAsOwnMessage = true; // Right side (teal)
                  }
                }
              } else {
                displayName = isOwnMessage ? 'You' : sender.name;
              }
              
              // Check if this is a call message
              const isCallMessage = message.type === 'call';
              const isEmergencyLocation = (message as any).type === 'emergency_location';
              const isEmergencyCall = isCallMessage && (message as any).isEmergency === true;

              return (
                <Fragment key={message.id}>
                  {_dividerLabel && (
                    <div className="flex items-center justify-center my-3" data-testid="chat-day-divider">
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 dark:bg-gray-800 dark:text-gray-400 px-3 py-1 rounded-full shadow-sm">
                        {_dividerLabel}
                      </span>
                    </div>
                  )}
                <div
                  id={`msg-${message.id}`}
                  className={`flex ${treatAsOwnMessage ? 'justify-end' : 'justify-start'} mb-2`}
                >
                  <div className={`flex ${treatAsOwnMessage ? 'flex-row-reverse' : 'flex-row'} items-start space-x-2 max-w-[80%] group`}>
                    {!treatAsOwnMessage ? (
                      <div className="flex-shrink-0 flex flex-col items-center gap-1">
                        {(() => {
                          // Determine which avatar to use based on conversation type and message sender
                          let avatarUrl: string | undefined;
                          
                          if (conversation.type === 'admin_client' && currentUser.role === 'client') {
                            // For client view of admin_client conversations, use business avatar for all staff messages
                            avatarUrl = businessDetails?.photoURL;
                          } else {
                            // For other conversation types, use sender's avatar
                            avatarUrl = sender.photoURL;
                          }
                          
                          return avatarUrl ? (
                            <img
                              src={avatarUrl}
                              alt={displayName}
                              className="w-8 h-8 rounded-full"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-sm font-medium text-gray-600">
                              {displayName.charAt(0).toUpperCase()}
                            </div>
                          );
                        })()}
                        {/* Save-to-Notes + Copy + Select-for-bundle stacked
                            under the avatar. Visible on hover for every
                            message (including own — see mirror block below). */}
                        {!isCallMessage && !(message as any).deleted && editingMessageId !== message.id && (
                          <div
                            className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-1"
                            data-testid={`message-actions-${message.id}`}
                          >
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => { setReplyTarget(message); inputRef.current?.focus(); }}
                              className="h-11 w-11 hover:bg-blue-100"
                              title="Reply to this message"
                              data-testid={`reply-message-btn-${message.id}`}
                            >
                              <Reply className="h-6 w-6 text-blue-600" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleSaveToNotes(message)}
                              disabled={savingNoteFor === message.id}
                              className="h-11 w-11 hover:bg-amber-100"
                              title="Save to client notes"
                              data-testid={`save-to-notes-btn-${message.id}`}
                            >
                              {savingNoteFor === message.id ? (
                                <Loader2 className="h-6 w-6 text-amber-600 animate-spin" />
                              ) : (
                                <Bookmark className="h-6 w-6 text-amber-600" />
                              )}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleCopyMessage(message)}
                              className="h-11 w-11 hover:bg-gray-200"
                              title="Copy text + timestamp to clipboard"
                              data-testid={`copy-message-btn-${message.id}`}
                            >
                              <Copy className="h-6 w-6 text-gray-600" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={(e) => toggleMessageSelection(message.id, (e as any).shiftKey)}
                              className={`h-11 w-11 ${selectedMessageIds.has(message.id) ? 'bg-teal-100 hover:bg-teal-200' : 'hover:bg-teal-50'}`}
                              title="Select (shift-click to range-select for multi-copy)"
                              data-testid={`select-message-btn-${message.id}`}
                            >
                              {selectedMessageIds.has(message.id) ? (
                                <CheckSquare className="h-6 w-6 text-teal-600" />
                              ) : (
                                <Square className="h-6 w-6 text-gray-500" />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      // Own-message side: no avatar, but render the same
                      // Save/Copy/Select stack on hover so the affordances
                      // mirror across the thread.
                      !isCallMessage && !(message as any).deleted && editingMessageId !== message.id && (
                        <div
                          className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center gap-1"
                          data-testid={`message-actions-${message.id}`}
                        >
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => { setReplyTarget(message); inputRef.current?.focus(); }}
                            className="h-11 w-11 hover:bg-blue-100"
                            title="Reply to this message"
                            data-testid={`reply-message-btn-${message.id}`}
                          >
                            <Reply className="h-6 w-6 text-blue-600" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleSaveToNotes(message)}
                            disabled={savingNoteFor === message.id}
                            className="h-11 w-11 hover:bg-amber-100"
                            title="Save to client notes"
                            data-testid={`save-to-notes-btn-${message.id}`}
                          >
                            {savingNoteFor === message.id ? (
                              <Loader2 className="h-6 w-6 text-amber-600 animate-spin" />
                            ) : (
                              <Bookmark className="h-6 w-6 text-amber-600" />
                            )}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleCopyMessage(message)}
                            className="h-11 w-11 hover:bg-gray-200"
                            title="Copy text + timestamp to clipboard"
                            data-testid={`copy-message-btn-${message.id}`}
                          >
                            <Copy className="h-6 w-6 text-gray-600" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={(e) => toggleMessageSelection(message.id, (e as any).shiftKey)}
                            className={`h-11 w-11 ${selectedMessageIds.has(message.id) ? 'bg-teal-100 hover:bg-teal-200' : 'hover:bg-teal-50'}`}
                            title="Select (shift-click to range-select for multi-copy)"
                            data-testid={`select-message-btn-${message.id}`}
                          >
                            {selectedMessageIds.has(message.id) ? (
                              <CheckSquare className="h-6 w-6 text-teal-600" />
                            ) : (
                              <Square className="h-6 w-6 text-gray-500" />
                            )}
                          </Button>
                        </div>
                      )
                    )}
                    <div className={`flex flex-col ${treatAsOwnMessage ? 'items-end' : 'items-start'}`}>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-sm font-medium text-gray-600">
                          {displayName}
                        </span>
                        <span className="text-sm text-gray-400">
                          {(() => {
                            // Smart timestamp: today = just the time,
                            // otherwise "Mon 13 July 9:53am" (weekday +
                            // date + full month + 12h time, no leading
                            // zero, no space before am/pm).
                            const d = message.createdAt as Date;
                            const now = new Date();
                            const fmtTime = (dt: Date) => {
                              const h = dt.getHours();
                              const m = dt.getMinutes();
                              const suffix = h < 12 ? 'am' : 'pm';
                              const h12 = h % 12 === 0 ? 12 : h % 12;
                              return `${h12}:${m.toString().padStart(2, '0')}${suffix}`;
                            };
                            const time = fmtTime(d);
                            const isSameDay =
                              d.getFullYear() === now.getFullYear() &&
                              d.getMonth() === now.getMonth() &&
                              d.getDate() === now.getDate();
                            if (isSameDay) return time;
                            // Weekday + day + full month (year appended
                            // only if the message is from a previous
                            // year, to keep labels short).
                            const inSameYear = ftz(d, 'yyyy') === ftz(now, 'yyyy');
                            const dateStr = ftz(d, inSameYear ? 'EEE d MMMM' : 'EEE d MMMM yyyy');
                            return `${dateStr} ${time}`;
                          })()}
                          {(message as any).edited && <span className="ml-1">(edited)</span>}
                        </span>
                      </div>
                      
                      {/* Message bubble with edit/delete options */}
                      <div className="relative group">
                        <div
                          className={`px-2 py-2 rounded-lg max-w-full break-words cursor-pointer select-none ${
                            isEmergencyLocation || isEmergencyCall
                              ? 'bg-red-600 text-white border-2 border-red-300'
                              : treatAsOwnMessage
                                ? 'bg-teal-500 text-white'
                                : 'bg-blue-500 text-white'
                          } ${selectedMessageIds.has(message.id) ? 'ring-2 ring-offset-2 ring-teal-400' : ''}`}
                          onMouseDown={(e) => {
                            // Don't trigger on edit mode or if clicking buttons
                            if (editingMessageId === message.id || (e.target as HTMLElement).closest('button, input')) {
                              return;
                            }
                            const timer = setTimeout(() => {
                              setReactionPickerMessageId(message.id);
                            }, 500); // 500ms long press
                            (e.currentTarget as any).longPressTimer = timer;
                          }}
                          onMouseUp={(e) => {
                            if ((e.currentTarget as any).longPressTimer) {
                              clearTimeout((e.currentTarget as any).longPressTimer);
                            }
                          }}
                          onMouseLeave={(e) => {
                            if ((e.currentTarget as any).longPressTimer) {
                              clearTimeout((e.currentTarget as any).longPressTimer);
                            }
                          }}
                          onTouchStart={(e) => {
                            // Don't trigger on edit mode or if clicking buttons
                            if (editingMessageId === message.id || (e.target as HTMLElement).closest('button, input')) {
                              return;
                            }
                            const timer = setTimeout(() => {
                              setReactionPickerMessageId(message.id);
                            }, 500); // 500ms long press
                            (e.currentTarget as any).longPressTimer = timer;
                          }}
                          onTouchEnd={(e) => {
                            if ((e.currentTarget as any).longPressTimer) {
                              clearTimeout((e.currentTarget as any).longPressTimer);
                            }
                          }}
                        >
                          {/* Quoted reply block (Messenger-style) */}
                          {(message as any).replyTo?.id && !isCallMessage && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const el = document.getElementById(`msg-${(message as any).replyTo.id}`);
                                if (el) {
                                  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  el.classList.add('ring-2', 'ring-amber-400', 'rounded-lg');
                                  setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'rounded-lg'), 1600);
                                }
                              }}
                              className={`block w-full text-left mb-1.5 px-2 py-1 rounded-md border-l-4 ${
                                treatAsOwnMessage
                                  ? 'bg-teal-700/50 border-teal-200'
                                  : 'bg-blue-700/50 border-blue-200'
                              }`}
                              title="Jump to original message"
                              data-testid={`reply-quote-${message.id}`}
                            >
                              <div className="text-[11px] font-semibold opacity-90 flex items-center gap-1">
                                <Reply className="h-3 w-3" />
                                {(message as any).replyTo.senderName || 'Original message'}
                              </div>
                              <div className="text-xs opacity-80 truncate">
                                {(message as any).replyTo.text}
                              </div>
                            </button>
                          )}
                          {(() => {
                            // ── Canonical chat-media resolution (matches the
                            // native team's brief — see chat-media-schema doc).
                            // - Images can be marked by `type === 'image'` OR
                            //   `fileType === 'image'`; URL may live on
                            //   `imageUrl` OR `attachmentUrl`.
                            // - Videos use `type === 'file'` + `fileType ===
                            //   'video'`; URL on `fileUrl` OR `attachmentUrl`.
                            // - Documents are `type === 'file'` with no video
                            //   fileType (or `fileType === 'document'`).
                            const m: any = message;
                            const looksImage = m.type === 'image' || m.fileType === 'image';
                            const looksVideo = m.fileType === 'video' || (m.type === 'file' && m.fileType === 'video');
                            const imageSrc = looksImage ? (m.imageUrl || m.attachmentUrl || null) : null;
                            const videoSrc = looksVideo ? (m.fileUrl || m.attachmentUrl || null) : null;
                            const looksFile = !imageSrc && !videoSrc && m.type === 'file' && (m.attachmentUrl || m.fileUrl);
                            const fileSrc = looksFile ? (m.attachmentUrl || m.fileUrl) : null;
                            const captionText = m.content || m.text;
                            const captionShow = captionText
                              && captionText !== '📷 Image'
                              && captionText !== '📹 Video';

                            if (isEmergencyLocation) {
                              // Red emergency-location card (written by the
                              // backend during an after-hours emergency call).
                              const loc = m.emergencyLocation || {};
                              const hasCoords = typeof loc.latitude === 'number' && typeof loc.longitude === 'number';
                              return (
                                <div className="flex flex-col gap-1.5" data-testid={`emergency-location-card-${message.id}`}>
                                  <div className="flex items-center gap-2 font-semibold">
                                    <MapPin className="h-5 w-5 shrink-0" />
                                    <span>Emergency location</span>
                                    {m.emergencyReason && (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-white/25 font-medium">{m.emergencyReason}</span>
                                    )}
                                  </div>
                                  <div className="whitespace-pre-wrap text-sm">{m.content}</div>
                                  {hasCoords && (
                                    <a
                                      href={`https://www.google.com/maps/search/?api=1&query=${loc.latitude},${loc.longitude}`}
                                      target="_blank" rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="inline-flex items-center gap-1 text-sm underline font-medium"
                                      data-testid={`emergency-location-map-${message.id}`}
                                    >
                                      <ExternalLink className="h-4 w-4" /> Open map
                                    </a>
                                  )}
                                </div>
                              );
                            }
                            if (isCallMessage) {
                              return (
                                <div className="flex items-center space-x-2 text-med">
                                  <Phone className="h-6 w-6" />
                                  <span>{message.content}</span>
                                  {isEmergencyCall && (
                                    <span className="text-xs px-1.5 py-0.5 rounded bg-white/25 font-semibold whitespace-nowrap">
                                      EMERGENCY{m.emergencyReason ? ` · ${m.emergencyReason}` : ''}
                                    </span>
                                  )}
                                </div>
                              );
                            }
                            if (imageSrc) {
                              const imgName = m.attachmentName || m.fileName || 'Image';
                              return (
                                <div className="flex flex-col space-y-1">
                                  <img
                                    src={imageSrc}
                                    alt={imgName}
                                    className="max-w-full max-h-64 rounded-lg cursor-pointer hover:opacity-90 object-contain"
                                    onClick={() => setViewerImage({ url: imageSrc, name: imgName })}
                                  />
                                  {captionShow && (
                                    <div className="whitespace-pre-wrap text-sm">{captionText}</div>
                                  )}
                                </div>
                              );
                            }
                            if (videoSrc) {
                              return (
                                <div className="flex flex-col space-y-1">
                                  <video
                                    src={videoSrc}
                                    controls
                                    poster={m.thumbnailUrl || undefined}
                                    preload={m.thumbnailUrl ? 'none' : 'metadata'}
                                    className="max-w-full max-h-64 min-w-[200px] rounded-lg bg-black"
                                    data-testid={`chat-video-${message.id}`}
                                  />
                                  {captionShow && (
                                    <div className="whitespace-pre-wrap text-sm">{captionText}</div>
                                  )}
                                </div>
                              );
                            }
                            if (fileSrc) {
                              const fileName = m.attachmentName || m.fileName || 'Download file';
                              return (
                                <div className="flex items-center space-x-2">
                                  <File className="h-5 w-5" />
                                  <a
                                    href={fileSrc}
                                    download={fileName}
                                    className="underline hover:no-underline"
                                  >
                                    {fileName}
                                  </a>
                                  <Download className="h-4 w-4" />
                                </div>
                              );
                            }
                            return null;
                          })() || (editingMessageId === message.id ? (
                            /* Edit mode */
                            <div className="flex items-center space-x-2 min-w-[250px]">
                              <Input
                                value={editedContent}
                                onChange={(e) => setEditedContent(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleEditMessage(message.id);
                                  } else if (e.key === 'Escape') {
                                    cancelEdit();
                                  }
                                }}
                                className="flex-1 bg-white text-black"
                                autoFocus
                              />
                              <Button 
                                size="icon" 
                                variant="ghost"
                                onClick={() => handleEditMessage(message.id)}
                                className="h-8 w-8 text-white hover:bg-teal-600"
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost"
                                onClick={cancelEdit}
                                className="h-8 w-8 text-white hover:bg-teal-600"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className={`whitespace-pre-wrap ${
                              // Check if message is emoji-only (no letters/numbers, only emojis and spaces)
                              /^[\p{Emoji}\s]+$/u.test(message.content) && message.content.trim().length <= 10
                                ? 'emoji-only'
                                : 'text-lg emoji-message'
                            }`}>
                              {message.content}
                            </div>
                          ))}
                        </div>
                        
                        {/* Hover toolbar — only Edit/Delete (own messages).
                            Save-to-Notes + Copy moved under the avatar
                            column above for both own + non-own messages. */}
                        {treatAsOwnMessage && !isCallMessage && !(message as any).deleted && editingMessageId !== message.id && (
                          <div
                            className="absolute -left-24 top-0 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1"
                            data-testid={`message-edit-actions-${message.id}`}
                          >
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => startEdit(message)}
                              className="h-9 w-9 hover:bg-gray-200"
                              title="Edit message"
                            >
                              <Edit2 className="h-5 w-5 text-gray-600" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleDeleteMessage(message.id)}
                              disabled={isDeleting === message.id}
                              className="h-9 w-9 hover:bg-red-100"
                              title="Delete message"
                            >
                              {isDeleting === message.id ? (
                                <Loader2 className="h-5 w-5 text-gray-600 animate-spin" />
                              ) : (
                                <Trash2 className="h-5 w-5 text-red-600" />
                              )}
                            </Button>
                          </div>
                        )}
                        
                        {/* Reaction Picker Popup - appears on long press */}
                        {reactionPickerMessageId === message.id && (
                          <div 
                            ref={reactionPickerRef}
                            className={`absolute bottom-full mb-2 bg-white rounded-full shadow-lg border border-gray-200 px-2 py-2 flex gap-1 z-50 ${
                              treatAsOwnMessage ? 'right-0' : 'left-0'
                            }`}
                          >
                            {['👍', '❤️', '😂', '😮', '😢', '😡'].map((emoji) => (
                              <button
                                key={emoji}
                                onClick={() => {
                                  handleToggleReaction(message.id, emoji);
                                  setReactionPickerMessageId(null);
                                }}
                                className="text-2xl hover:scale-125 transition-transform cursor-pointer p-1 flex-shrink-0"
                                title={emoji === '👍' ? 'Like' : emoji === '❤️' ? 'Love' : emoji === '😂' ? 'Haha' : emoji === '😮' ? 'Wow' : emoji === '😢' ? 'Sad' : 'Angry'}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Display reactions below message bubble */}
                      {message.reactions && Object.keys(message.reactions).length > 0 && (
                        <div className={`flex items-center gap-1 mt-1 ${treatAsOwnMessage ? 'justify-end' : 'justify-start'}`}>
                          <div className="bg-white border border-gray-200 rounded-full px-2 py-0.5 flex items-center gap-1 shadow-sm">
                            <span className="text-base">{message.reactions[Object.keys(message.reactions)[0]]}</span>
                            <span className="text-xs text-gray-600 font-medium">{Object.keys(message.reactions).length}</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Read receipt indicator - avatars like Facebook Messenger */}
                      {/* Only show on the LAST message that each person has read */}
                      {treatAsOwnMessage && conversation.participants.length > 1 && (
                        <div className="mt-1 flex items-center gap-0.5">
                          {(() => {
                            // Find participants whose last read message is THIS message
                            const readersAtThisMessage: string[] = [];
                            lastReadByUser.forEach((lastMsgId, usrId) => {
                              if (lastMsgId === message.id) {
                                readersAtThisMessage.push(usrId);
                              }
                            });
                            
                            // Only show if this is the last read message for someone
                            if (readersAtThisMessage.length === 0) {
                              return null;
                            }
                            
                            // For client view in admin_client conversations, show business avatar for all staff
                            if (conversation.type === 'admin_client' && currentUser.role === 'client') {
                              // Group all staff readers together and show single business avatar
                              const hasStaffRead = readersAtThisMessage.some((userId: string) => {
                                const reader = participantDetails[userId];
                                return reader && (reader.role === 'admin' || reader.role === 'staff');
                              });
                              
                              if (!hasStaffRead) return null;
                              
                              // Show business avatar for all staff
                              return (
                                <div 
                                  className="w-4 h-4 rounded-full border border-white overflow-hidden flex-shrink-0"
                                  title="Read by TAP Staff"
                                >
                                  {businessDetails?.photoURL ? (
                                    <img
                                      src={businessDetails.photoURL}
                                      alt="TAP Staff"
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-teal-500 flex items-center justify-center text-[6px] font-medium text-white">
                                      T
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            
                            // For staff/admin view, show individual avatars for readers at this message
                            return (
                              <>
                                {readersAtThisMessage.slice(0, 3).map((userId: string) => {
                                  const reader = participantDetails[userId];
                                  if (!reader) return null;
                                  
                                  return (
                                    <div 
                                      key={userId} 
                                      className="w-4 h-4 rounded-full border border-white -ml-1 first:ml-0 overflow-hidden flex-shrink-0"
                                      title={`Read by ${reader.name}`}
                                    >
                                      {reader.photoURL ? (
                                        <img
                                          src={reader.photoURL}
                                          alt={reader.name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-full h-full bg-teal-500 flex items-center justify-center text-[6px] font-medium text-white">
                                          {reader.name.charAt(0).toUpperCase()}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {readersAtThisMessage.length > 3 && (
                                  <span className="text-[8px] text-gray-400 ml-1 font-medium">
                                    +{readersAtThisMessage.length - 3}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                </Fragment>
              );
            });
            })()
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Fixed Input Area - stays above keyboard on mobile */}
      <div 
        ref={inputContainerRef}
        className="flex-shrink-0 p-3 sm:p-4 border-t border-gray-200 bg-white relative"
        style={{
          // Ensure input area is always visible on mobile
          position: isKeyboardVisible ? 'sticky' : 'relative',
          bottom: 0,
          zIndex: 10
        }}
      >
        {/* Emoji Picker */}
        {showEmojiPicker && !disableInput && (
          <div 
            ref={emojiPickerRef}
            className="absolute bottom-full left-0 mb-2 z-50"
          >
            <EmojiPicker 
              onEmojiClick={handleEmojiClick}
              {...({ searchDisabled: false, width: 320, height: 400, emojiSize: 30 } as any)}
            />
          </div>
        )}

        {/* Reply-compose banner (Messenger-style) */}
        {replyTarget && (
          <div
            className="mb-2 px-3 py-2 bg-gray-50 border-l-4 border-teal-500 rounded-md flex items-start justify-between gap-2"
            data-testid="reply-compose-banner"
          >
            <div className="min-w-0">
              <div className="text-xs font-semibold text-teal-700 flex items-center gap-1">
                <Reply className="h-3.5 w-3.5" />
                Replying to {replyTarget.senderId === currentUser.id ? 'yourself' : replyTarget.senderName}
              </div>
              <div className="text-xs text-gray-600 truncate">{messageSnippet(replyTarget)}</div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0"
              onClick={() => setReplyTarget(null)}
              data-testid="reply-compose-cancel"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}

        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,application/pdf,.doc,.docx,.txt"
            className="hidden"
            onChange={handleFileSelect}
            disabled={disableInput}
          />
          
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isSending || isUploading || disableInput}
            className="flex-shrink-0 h-9 w-9 p-0"
          >
            <Paperclip className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={isSending || isUploading || disableInput}
            className="flex-shrink-0 h-9 w-9 p-0"
          >
            <Smile className="h-4 w-4" />
          </Button>
          
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={() => setShowEmojiPicker(false)}
            placeholder={disableReason || "Type a message..."}
            disabled={isSending || isUploading || disableInput}
            className="flex-1 min-w-0 px-2 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-base"
          />
          
          {/* Quick emoji reaction button - shows when input is empty */}
          {!newMessage.trim() && !selectedImage && (
            <Button
              onClick={async () => {
                if (isSending || disableInput) return;
                
                setIsSending(true);
                try {
                  await sendMessage(
                    conversation.id,
                    '👍',
                    currentUser
                  );
                  setError(null);
                } catch (error) {
                  console.error('Error sending quick reaction:', error);
                  setError(error instanceof Error ? error.message : 'Failed to send reaction');
                  toast.error('Failed to send reaction');
                } finally {
                  setIsSending(false);
                }
              }}
              disabled={isSending || isUploading || disableInput}
              size="sm"
              variant="ghost"
              className="flex-shrink-0 h-8 w-8 p-0 text-xl hover:bg-teal-50 transition-transform hover:scale-110"
              title="Send thumbs up"
            >
              👍
            </Button>
          )}
          
          <Button
            onClick={handleSendMessage}
            disabled={(!newMessage.trim() && !selectedImage) || isSending || isUploading || disableInput}
            size="sm"
            className="flex-shrink-0 h-8 w-8 sm:w-auto sm:px-4 p-0 sm:p-2"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
        
        {/* Selected image preview */}
        {selectedImage && (
          <div className="mt-2 p-2 bg-gray-50 rounded-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <img 
                src={selectedImage.url} 
                alt={selectedImage.name}
                className="w-12 h-12 object-cover rounded"
              />
              <span className="text-sm text-gray-600 truncate">{selectedImage.name}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedImage(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        {/* Upload progress */}
        {isUploading && (
          <div className="mt-2 text-sm text-teal-600 flex items-center space-x-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Uploading file...</span>
          </div>
        )}
      </div>

      {/* Image Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0">
          <DialogHeader className="sr-only">
            <DialogTitle>View Image</DialogTitle>
          </DialogHeader>
          <div className="p-4">
            {selectedImage && (
              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="max-w-full max-h-[70vh] mx-auto object-contain rounded-lg"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox for clicking on a sent/received image bubble. Replaces
          the old `window.open(...)` new-tab behaviour with an in-app
          modal so the user stays in the chat thread context. Click
          outside or press Escape to close. */}
      <Dialog open={!!viewerImage} onOpenChange={(open) => { if (!open) setViewerImage(null); }}>
        <DialogContent className="max-w-5xl max-h-[95vh] p-0 bg-transparent border-0 shadow-none">
          <DialogHeader className="sr-only">
            <DialogTitle>{viewerImage?.name || 'Image'}</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center w-full h-full p-2">
            {viewerImage && (
              <img
                src={viewerImage.url}
                alt={viewerImage.name}
                className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl bg-white"
                data-testid="chat-image-viewer"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
